import { ColumnType, DataCell, TableColumn } from '@ecosystem/export-api';
import { ExportPerformanceBySegmentResp } from '@data-visualization/performance-analysis-line-chart/Types';
import { PageName } from '@utils/pageConfig/Types';
import {
  ColumnHeader as ColumnHeaderProducts,
  ColumnHeaderDisplayName as ColumnHeaderDisplayNameProducts,
  ProductsCompareExport,
  Symbol,
} from '@models/Products';
import { getFormattedPercentageColumn } from '../export-table/ExportTableExcel';
import { formatPerformanceAnalysisLineChartExcel } from '@hooks/export/export-line-chart/PerformanceAnalysisLineChartExcelFormatter';

export const getLineChartExportColumn = (
  data: any,
  showPerformanceWithin: string,
  pageName?: PageName,
): TableColumn[] => {
  if (pageName === PageName.RI_PRODUCT_TRENDS) {
    return getProductsTrendsLineChartExportColumn(data);
  }
  return getPerformanceAnalysisLineChartExportColumn(data, showPerformanceWithin);
};

const getPerformanceAnalysisLineChartExportColumn = (
  data: ExportPerformanceBySegmentResp,
  showPerformanceWithin: string,
): TableColumn[] => {
  const periodColumns = data.kpiDataDto[0].kpiData[0].marketAverage.map((marketValue) => {
    return {
      header: marketValue.period,
    };
  });
  return [{ header: showPerformanceWithin }, { header: 'KPI' }, ...periodColumns];
};

const getProductsTrendsLineChartExportColumn = (data: ProductsCompareExport[]): TableColumn[] => {
  const periodColumns = data[0].itemContents.map((itemContent) => {
    return {
      header: itemContent.period,
    };
  });
  return [{ header: 'Model' }, { header: 'Brand' }, { header: 'KPI' }, ...periodColumns];
};

export const getLineChartExportRow = (
  data: any,
  currencyLabel: string,
  currencySymbol: string,
  pageName?: PageName,
  volume?: string,
): DataCell[][] => {
  if (pageName === PageName.RI_PRODUCT_TRENDS) {
    return getProductsTrendsLineChartExportRow(data, currencySymbol);
  }

  return formatPerformanceAnalysisLineChartExcel(data, currencyLabel, volume);
};

type ModelBrandAndKpiColumnHeaderParamFuncType = (
  model: string,
  brand: string,
) => (columnHeaderProducts: ColumnHeaderProducts, symbol?: string) => DataCell[];

/**
 * returns a function to form Column Header with Model, Brand and KPI values
 * @param model Model column
 * @param brand Brand column
 */
const getCompareProductsModelBrandAndKpiColumnHeader: ModelBrandAndKpiColumnHeaderParamFuncType =
  (model, brand) =>
  (columnHeaderProducts, symbol = '') =>
    [
      {
        value: model,
        type: ColumnType.Text,
      },
      {
        value: brand,
        type: ColumnType.Text,
      },
      {
        value: `${ColumnHeaderDisplayNameProducts[columnHeaderProducts]} ${symbol}`,
        type: ColumnType.Text,
      },
    ];

const getProductsTrendsLineChartExportRow = (data: ProductsCompareExport[], currencySymbol: string): DataCell[][] => {
  const outputData: DataCell[][] = [];

  data.forEach((item) => {
    const getCompareProductsKpiColumnHeader = getCompareProductsModelBrandAndKpiColumnHeader(
      item.item.itemName,
      item.item.itemBrand,
    );

    // Market Revenue Row
    const marketRevenuePeriodValues: DataCell[] = item.itemContents.map((content) => ({
      value: content.marketRevenue.value,
      type: ColumnType.Currency,
    }));
    const marketRevenue: DataCell[] = [
      ...getCompareProductsKpiColumnHeader(ColumnHeaderProducts.marketRevenue, `${currencySymbol}`),
      ...marketRevenuePeriodValues,
    ];

    //Market Units row
    const marketUnitsPeriodValue: DataCell[] = item.itemContents.map((content) => ({
      value: content.marketVolume.value,
      type: ColumnType.Number,
    }));
    const marketUnits: DataCell[] = [
      ...getCompareProductsKpiColumnHeader(ColumnHeaderProducts.marketVolume),
      ...marketUnitsPeriodValue,
    ];

    // Market Price row
    const marketPricePeriodValue: DataCell[] = item.itemContents.map((content) => ({
      value: content.marketPrice.value,
      type: ColumnType.Currency,
    }));
    const marketPrice: DataCell[] = [
      ...getCompareProductsKpiColumnHeader(ColumnHeaderProducts.marketPrice, `${currencySymbol}`),
      ...marketPricePeriodValue,
    ];

    // Retailer Revenue row
    const retailerRevenuePeriodValue: DataCell[] = item.itemContents.map((content) => ({
      value: content.retailerRevenue.value,
      type: ColumnType.Currency,
    }));
    const retailerRevenue: DataCell[] = [
      ...getCompareProductsKpiColumnHeader(ColumnHeaderProducts.retailerRevenue, `${currencySymbol}`),
      ...retailerRevenuePeriodValue,
    ];

    // Retailer Revenue Share row
    const retailerRevenueSharePeriodValue: DataCell[] = item.itemContents.map((content) =>
      getFormattedPercentageColumn(content.retailerShareRevenue.sharePercentage),
    );
    const retailerRevenueShare: DataCell[] = [
      ...getCompareProductsKpiColumnHeader(ColumnHeaderProducts.retailerShareRevenue, Symbol.PERCENTAGE),
      ...retailerRevenueSharePeriodValue,
    ];

    // Retailer Units row
    const retailerUnitsPeriodValue: DataCell[] = item.itemContents.map((content) => ({
      value: content.retailerVolume.value,
      type: ColumnType.Number,
    }));
    const retailerUnits: DataCell[] = [
      ...getCompareProductsKpiColumnHeader(ColumnHeaderProducts.retailerVolume),
      ...retailerUnitsPeriodValue,
    ];

    // Retailer Units Share row
    const retailerUnitsSharePeriodValue: DataCell[] = item.itemContents.map((content) =>
      getFormattedPercentageColumn(content.retailerShareVolume.sharePercentage),
    );
    const retailerUnitsShare: DataCell[] = [
      ...getCompareProductsKpiColumnHeader(ColumnHeaderProducts.retailerShareVolume, Symbol.PERCENTAGE),
      ...retailerUnitsSharePeriodValue,
    ];

    // Retailer Price row
    const retailerPricePeriodValue: DataCell[] = item.itemContents.map((content) => ({
      value: content.retailerPrice.value,
      type: ColumnType.Currency,
    }));
    const retailerPrice: DataCell[] = [
      ...getCompareProductsKpiColumnHeader(ColumnHeaderProducts.retailerPrice, `${currencySymbol}`),
      ...retailerPricePeriodValue,
    ];

    // Retailer Revenue Mix row
    const retailerRevenueMixPeriodValue: DataCell[] = item.itemContents.map((content) =>
      getFormattedPercentageColumn(content.retailerMixRevenue.sharePercentage),
    );
    const retailerRevenueMix: DataCell[] = [
      ...getCompareProductsKpiColumnHeader(ColumnHeaderProducts.retailerMixRevenue, Symbol.PERCENTAGE),
      ...retailerRevenueMixPeriodValue,
    ];

    // Retailer Unit Mix row
    const retailerUnitMixPeriodValue: DataCell[] = item.itemContents.map((content) =>
      getFormattedPercentageColumn(content.retailerMixVolume.sharePercentage),
    );
    const retailerUnitMix: DataCell[] = [
      ...getCompareProductsKpiColumnHeader(ColumnHeaderProducts.retailerMixVolume, Symbol.PERCENTAGE),
      ...retailerUnitMixPeriodValue,
    ];

    outputData.push(
      marketRevenue,
      marketUnits,
      marketPrice,
      retailerRevenue,
      retailerRevenueShare,
      retailerUnits,
      retailerUnitsShare,
      retailerPrice,
      retailerRevenueMix,
      retailerUnitMix,
    );
  });

  return outputData;
};
